import {
  DEFAULT_CURRENCY_CODE,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl-BE';
import localeFR from '@angular/common/locales/fr-BE';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter } from '@angular/material/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import moment, { LongDateFormatSpec, Moment } from 'moment';
import { Device } from '@capacitor/device';

import './moment-locales';
import { HttpLoaderFactory } from './http-loader.factory';
import {
  FalconLanguages,
  LOCAL_STORAGE_FALCON_LANG,
  LOCAL_STORAGE_FALCON_LANG_CHANGED,
} from './language';

const DATE_FORMAT_CONFIG: LongDateFormatSpec = {
  LT: 'HH:mm',
  LTS: 'HH:mm:ss',
  L: 'DD-MM-YYYY',
  LL: 'DD MMMM YYYY',
  LLL: 'DD MMMM YYYY HH:mm',
  LLLL: 'dddd DD MMMM YYYY HH:mm',
};

export function provideI18n(): EnvironmentProviders {
  return makeEnvironmentProviders([
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideMomentDateAdapter({
      parse: {
        dateInput: ['l', 'LL'],
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
      },
    }),
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    // Runs once when the root injector is created, exactly where the former I18nModule constructor ran
    provideEnvironmentInitializer(initI18n),
  ]);
}

function initI18n(): void {
  const translateService = inject(TranslateService);
  const dateAdapter = inject<DateAdapter<Moment>>(DateAdapter);

  registerLocaleData(localeNl, FalconLanguages.NL);
  registerLocaleData(localeFR, FalconLanguages.FR);
  translateService.langs.push(...Object.values(FalconLanguages));

  translateService.onLangChange.subscribe(({ lang }) => {
    document.documentElement.lang = lang;
    localStorage.setItem(LOCAL_STORAGE_FALCON_LANG, lang);
    dateAdapter.setLocale(lang);
    moment.locale(lang);
  });

  moment.updateLocale(FalconLanguages.EN, {
    week: { dow: 1 },
    longDateFormat: DATE_FORMAT_CONFIG,
  });

  moment.updateLocale(FalconLanguages.FR, {
    longDateFormat: DATE_FORMAT_CONFIG,
  });

  initLanguage(translateService);
}

async function initLanguage(translateService: TranslateService) {
  const saved = localStorage.getItem(LOCAL_STORAGE_FALCON_LANG);
  const userChanged = localStorage.getItem(LOCAL_STORAGE_FALCON_LANG_CHANGED) === 'true';

  const deviceLang = await detectDeviceLanguage();
  const normalizedDeviceLang = deviceLang?.split('-')[0];

  const supported = translateService.langs;
  const fallback = FalconLanguages.EN;

  let chosen: string;

  if (!saved || !userChanged) {
    chosen = supported.includes(normalizedDeviceLang) ? normalizedDeviceLang : fallback;
  } else {
    chosen = saved;
  }

  translateService.setDefaultLang(chosen);
  translateService.use(chosen);

  localStorage.setItem(LOCAL_STORAGE_FALCON_LANG, chosen);
}

async function detectDeviceLanguage(): Promise<string> {
  try {
    const info = await Device.getLanguageCode();
    return info.value;
  } catch {
    if (navigator.languages && navigator.languages.length) {
      return navigator.languages[0];
    }
    return navigator.language;
  }
}
